import { FunctionComponent, MouseEvent, useCallback, useState } from 'react';

import { useAccountGet } from '@sbv-clients/data-access-api';
import { useAuth } from '@sbv-clients/shared-feature-auth';
import {
  Chip,
  Icon,
  LetterAvatar,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
  useLoading,
  useTheme,
} from '@sbv-clients/ui-shared';

import { UserDropdownProps } from './typings/user-dropdown-props';

export const UserDropdown: FunctionComponent<UserDropdownProps> = ({
  ssoUserName,
  extraMenuItems,
}) => {
  const {
    isLoading: signOutLoading,
    showLoading: showSignOutLoading,
    hideLoading: hideSignOutLoading,
  } = useLoading();
  const { username, onSignOut } = useAuth();
  const { data: accountData } = useAccountGet();
  const isSupportSession = accountData?.isSupportSession ?? false;
  const supportUsername = accountData?.username ?? username;
  const displayUsername = ssoUserName || username;
  const [anchorMenu, setAnchorMenu] = useState<Element | null>(null);
  const toggleMenu = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorMenu(event.currentTarget);
  }, []);

  const closeMenu = useCallback(() => {
    setAnchorMenu(null);
  }, []);

  const theme = useTheme();

  const handleSignOut = useCallback(async () => {
    try {
      showSignOutLoading();
      await onSignOut();
    } catch (e) {
      /* empty */
    } finally {
      hideSignOutLoading();
    }
  }, [hideSignOutLoading, onSignOut, showSignOutLoading]);

  return (
    <>
      <LetterAvatar
        name={displayUsername}
        sx={{
          width: '32px',
          height: '32px',
          background: theme.colors.primary.main,
          color: theme.background.white,
        }}
        onClick={toggleMenu}
      />
      <Menu
        anchorEl={anchorMenu}
        open={Boolean(anchorMenu)}
        onClose={closeMenu}
        sx={{ marginRight: '20px', paddingRight: '20px' }}
      >
        <MenuItem onClick={closeMenu}>
          <ListItemIcon>
            <Icon
              name={'Account'}
              customSizes={{
                width: 24,
                height: 24,
                color: theme.text.darkText,
              }}
            />
          </ListItemIcon>
          {isSupportSession ? (
            <Chip
              label={`Support: ${supportUsername}`}
              size="small"
              color="primary"
              variant="outlined"
            />
          ) : (
            <Typography fontWeight="bold">{displayUsername}</Typography>
          )}
        </MenuItem>
        {extraMenuItems?.map(({ label, icon, onClick }) => (
          <MenuItem
            key={label}
            onClick={() => {
              closeMenu();
              onClick();
            }}
          >
            <ListItemIcon>{icon}</ListItemIcon>
            <Typography fontWeight="bold">{label}</Typography>
          </MenuItem>
        ))}
        <MenuItem onClick={handleSignOut} disabled={signOutLoading}>
          <ListItemIcon>
            <Icon
              name={'Logout'}
              fontSize="small"
              customSizes={{ width: 24, height: 24 }}
            />
          </ListItemIcon>
          <Typography fontWeight="bold">Sign Out</Typography>
        </MenuItem>
      </Menu>
    </>
  );
};
